import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Upload, Stamp } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TVA_RATES } from "@/lib/constants";

const FIELDS = [
  { k: "name", label: "Nom de l'entreprise" },
  { k: "address", label: "Adresse" },
  { k: "siret", label: "SIRET" },
  { k: "phone", label: "Téléphone" },
  { k: "email", label: "Email" },
  { k: "iban", label: "IBAN" },
];

export default function Settings() {
  const [s, setS] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);

  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image trop lourde (max 2 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => setS((v) => ({ ...v, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Paramètres enregistrés");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!s) return <div className="p-8 text-slate-500">Chargement…</div>;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Paramètres</h1>
        <p className="text-slate-600">Coordonnées et branding affichés sur les PDF</p>
      </div>

      {/* Logo */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold">Logo</h2>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-48 h-24 border border-slate-200 rounded-md grid place-items-center bg-slate-50 overflow-hidden">
            {s.logo_base64 ? (
              <img src={s.logo_base64} alt="Logo" data-testid="logo-preview" className="max-h-full max-w-full object-contain" />
            ) : (
              <span className="text-slate-400 text-sm">Aucun logo</span>
            )}
          </div>
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" data-testid="logo-file-input" className="hidden" onChange={onLogo} />
            <Button variant="outline" data-testid="upload-logo-button" onClick={() => fileRef.current?.click()} className="h-12 border-[#0284C7] text-[#0284C7] font-bold">
              <Upload size={18} className="mr-1" /> Choisir un logo (PNG/JPG)
            </Button>
            {s.logo_base64 && (
              <Button variant="ghost" data-testid="remove-logo-button" onClick={() => setS((v) => ({ ...v, logo_base64: "" }))} className="h-12 text-red-600 block">
                Retirer le logo
              </Button>
            )}
            <p className="text-xs text-slate-500">Il apparaîtra en-tête des devis et factures. Max 2 Mo.</p>
          </div>
        </div>
      </div>

      {/* Coordonnées */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold">Coordonnées</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.k} className={`space-y-2 ${f.k === "address" || f.k === "iban" ? "md:col-span-2" : ""}`}>
              <Label>{f.label}</Label>
              <Input data-testid={`settings-${f.k}`} className="h-12" value={s[f.k] || ""} onChange={set(f.k)} />
            </div>
          ))}
        </div>
      </div>

      {/* Facturation / TVA */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold">Facturation — TVA</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Taux de TVA par défaut</Label>
            <Select value={String(s.default_tva_rate ?? 0)} onValueChange={(v) => setS((x) => ({ ...x, default_tva_rate: Number(v) }))}>
              <SelectTrigger data-testid="settings-default-tva" className="h-12"><SelectValue /></SelectTrigger>
              <SelectContent>{TVA_RATES.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Appliqué aux nouvelles lignes de devis/factures. Les documents déjà créés ne changent pas.</p>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Mention légale d'exonération de TVA (affichée sur le PDF quand la TVA est à 0)</Label>
          <Textarea data-testid="settings-tva-mention" value={s.tva_exemption_mention || ""} onChange={set("tva_exemption_mention")} placeholder="TVA non applicable, art. 293 B du CGI" />
        </div>
      </div>

      {/* PDF / cachet */}
      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2"><Stamp size={20} className="text-[#16A34A]" /> Cachet & mentions PDF</h2>
        <div className="space-y-2">
          <Label>Texte du cachet (format « Ligne 1 – Ligne 2 »)</Label>
          <Input data-testid="settings-stamp-text" className="h-12" value={s.stamp_text || ""} onChange={set("stamp_text")} placeholder="MS ÉNERGIE – Validé" />
        </div>
        <div className="space-y-2">
          <Label>Conditions de paiement (pied de page PDF)</Label>
          <Textarea data-testid="settings-payment-terms" value={s.payment_terms || ""} onChange={set("payment_terms")} />
        </div>
        <div className="space-y-2">
          <Label>Garantie installation (pied de page PDF)</Label>
          <Textarea data-testid="settings-warranty" value={s.warranty || ""} onChange={set("warranty")} />
        </div>
      </div>

      <Button data-testid="save-settings-button" onClick={save} className="h-12 bg-[#16A34A] hover:bg-[#15803D] font-bold w-full sm:w-auto"><Save size={18} className="mr-1" />Enregistrer</Button>
    </div>
  );
}
